import { useEffect, useState } from 'react';
import Link from '@/components/compat/Link';
import { ArrowRight, Loader2 } from 'lucide-react';
import { getAceBySlug, type AceInfo } from '@/lib/aces-data';
import { useCatalogAces } from '@/components/painel/aces/aces-cms-context';
import * as acesService from '@/services/aces.service';
import { isPubliclyVisible } from '@/lib/cms-publication';
import { formatAceDisplayName, ACE_ICON_SHELL_STYLE } from '@/components/help-center/aces/ace-shared';
import { GsImage, XatsPriceValue, cn } from '@/components/help-center/latest-power-shared';
import { stripHtmlToText } from '@/lib/cms-rich-helpers';

export function AceMentionPreview({
  data,
  loading,
  variant = 'popover',
  onNavigate,
}: {
  data: AceInfo | null;
  loading?: boolean;
  variant?: 'popover' | 'sheet';
  onNavigate?: () => void;
}) {
  const displayName = data ? formatAceDisplayName(data.slug) : '';
  const shortDesc = data ? stripHtmlToText(data.shortDescription) : '';

  return (
    <div
      className={cn(
        'flex flex-col text-amber-50',
        variant === 'popover' ? 'p-4' : 'px-1 pb-2 pt-1'
      )}
    >
      {loading ? (
        <div className="flex min-h-[120px] items-center justify-center gap-2 text-sm text-amber-100/60">
          <Loader2 className="h-4 w-4 animate-spin text-amber-400" aria-hidden />
          A carregar…
        </div>
      ) : !data ? (
        <p className="py-6 text-center text-sm text-amber-100/60">Ace não encontrado.</p>
      ) : (
        <>
          <div className="flex gap-3">
            <div
              className="flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-2xl"
              style={ACE_ICON_SHELL_STYLE}
            >
              <GsImage
                code={data.smileyCode || data.slug}
                className="flex h-full w-full items-center justify-center"
                imgClassName="max-h-[56px] max-w-[56px] object-contain"
              />
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <span className="inline-flex items-center rounded-full border border-amber-500/35 bg-amber-950/50 px-2.5 py-0.5 font-poppins text-[10px] font-bold uppercase tracking-wide text-amber-200">
                Ace
              </span>
              <h3 className="mt-2 font-poppins text-base font-bold leading-tight text-white">
                {displayName}
              </h3>
              {shortDesc ? (
                <p className="mt-1.5 line-clamp-3 font-inter text-[13px] leading-relaxed text-amber-50/85">
                  {shortDesc}
                </p>
              ) : null}
            </div>
          </div>

          <div className="my-3 h-px bg-gradient-to-r from-transparent via-amber-400/30 to-transparent" />
          <div className="flex items-center justify-between gap-3 text-xs">
            <span className="text-amber-100/55">Assinatura</span>
            <span className="inline-flex items-center gap-1 font-medium text-amber-100">
              <XatsPriceValue className="text-amber-100">{data.storePriceXats}</XatsPriceValue>
              <span className="text-amber-200/70">xats / {data.subscriptionDays}d</span>
            </span>
          </div>

          <Link
            href={`/aces/${data.slug}`}
            onClick={onNavigate}
            className={cn(
              'mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5',
              'bg-gradient-to-r from-amber-600 via-amber-500 to-yellow-500',
              'font-poppins text-xs font-semibold text-[#1a1002]',
              'shadow-[0_0_20px_rgba(245,158,11,0.35)] transition hover:brightness-110'
            )}
          >
            Ver Ace
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </>
      )}
    </div>
  );
}

function useAceMentionData(slug: string, enabled: boolean) {
  const key = slug.trim().toLowerCase();
  const { aces } = useCatalogAces();
  const fromCatalog = aces.find((a) => a.slug.toLowerCase() === key) ?? getAceBySlug(key) ?? null;
  const [remote, setRemote] = useState<AceInfo | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || fromCatalog) {
      setRemote(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void acesService
      .getPublicAce(key)
      .then((row) => {
        if (cancelled) return;
        if (row && isPubliclyVisible(row.publicationStatus)) setRemote(row);
        else setRemote(null);
      })
      .catch(() => {
        if (!cancelled) setRemote(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key, enabled, fromCatalog]);

  return { data: fromCatalog ?? remote, loading: Boolean(enabled && !fromCatalog && loading) };
}

export const aceMentionLinkClass = cn(
  'font-medium text-amber-600 decoration-amber-500/40 underline-offset-2',
  'transition-[color,text-decoration-color] duration-200',
  'hover:text-amber-500 hover:underline',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50 focus-visible:ring-offset-1',
  'cursor-pointer'
);

export default function AceMention({
  slug,
  children,
  className,
}: {
  slug: string;
  children?: React.ReactNode;
  className?: string;
}) {
  const key = slug.trim().toLowerCase();
  const label = children ?? formatAceDisplayName(key);
  const [open, setOpen] = useState(false);
  const [prefetch, setPrefetch] = useState(false);
  const { data, loading } = useAceMentionData(key, prefetch || open);

  // Lightweight hover without full HoverCard dependency for bundle simplicity —
  // reuse CSS popover pattern similar to powers when possible.
  return (
    <span
      className="relative inline"
      onMouseEnter={() => {
        setPrefetch(true);
        setOpen(true);
      }}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => {
        setPrefetch(true);
        setOpen(true);
      }}
      onBlur={() => setOpen(false)}
    >
      <Link href={`/aces/${key}`} className={cn(aceMentionLinkClass, className)}>
        {label}
      </Link>
      {open ? (
        <span
          className={cn(
            'absolute bottom-full left-1/2 z-50 mb-2 w-[min(100vw-2rem,330px)] -translate-x-1/2',
            'overflow-hidden rounded-2xl border border-amber-500/30 bg-[#1a1206]/96 backdrop-blur-xl',
            'shadow-[0_8px_40px_rgba(0,0,0,0.55),0_0_24px_rgba(245,158,11,0.18)]'
          )}
          role="tooltip"
        >
          <AceMentionPreview data={data} loading={loading} />
        </span>
      ) : null}
    </span>
  );
}
